import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createContact, ContactCategory } from "@/services/customerService";
import { Helmet } from 'react-helmet-async';
import { Mail, CheckCircle, Send, Loader2, User, Phone, Tag, FileText, MessageSquare } from "lucide-react";

const CATEGORY_OPTIONS: { value: ContactCategory; label: string }[] = [
  { value: 'general', label: '一般的なお問い合わせ' },
  { value: 'technical', label: '技術的な問題' },
  { value: 'billing', label: '請求・支払い' },
  { value: 'account', label: 'アカウント関連' },
  { value: 'feature_request', label: '機能リクエスト' },
  { value: 'bug_report', label: 'バグ報告' },
  { value: 'other', label: 'その他' },
];

type ContactPageProps = {
  contactUrl?: string | null;
};

const ContactPage: React.FC<ContactPageProps> = ({ contactUrl }) => {
  const [category, setCategory] = useState<ContactCategory>('general');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [contactNumber, setContactNumber] = useState<string>('');

  // 外部お問い合わせURLが設定されている場合はリダイレクト
  useEffect(() => {
    if (contactUrl && contactUrl.startsWith('http')) {
      window.location.href = contactUrl;
    }
  }, [contactUrl]);

  if (contactUrl && contactUrl.startsWith('http')) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const formData = {
      name: (e.currentTarget.elements.namedItem('name') as HTMLInputElement).value,
      email: (e.currentTarget.elements.namedItem('email') as HTMLInputElement).value,
      phone: (e.currentTarget.elements.namedItem('phone') as HTMLInputElement).value || undefined,
      category,
      subject: (e.currentTarget.elements.namedItem('subject') as HTMLInputElement).value,
      message: (e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement).value,
    };

    try {
      const response = await createContact(formData);
      setContactNumber(response.contact_number);
      setIsSuccess(true);
      // フォームをリセット
      (e.target as HTMLFormElement).reset();
    } catch (err: any) {
      setError(err.response?.data?.errors?.join(', ') || 'お問い合わせの送信に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <>
        <Helmet>
          <title>お問い合わせ完了</title>
          <meta property="og:title" content="お問い合わせ完了" />
        </Helmet>

        <div className="min-h-screen bg-theme-bg">
          <div className="pt-20">
            <div className="max-w-screen-lg mx-auto px-4 py-8">
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8 text-center">
                <div className="w-16 h-16 bg-green-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                  <CheckCircle className="w-8 h-8 text-black" />
                </div>
                <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-black mb-4">お問い合わせを受け付けました</h1>
                <div className="inline-block px-4 py-2 bg-gray-100 border-theme rounded-theme mb-2">
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">お問い合わせ番号</p>
                  <p className="font-mono [font-weight:var(--theme-font-weight)] text-lg text-black">{contactNumber}</p>
                </div>
                <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600 mb-6">
                  お問い合わせいただきありがとうございます。
                </p>
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setCategory('general');
                  }}
                  className="px-6 py-3 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight)] shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                >
                  別のお問い合わせを送信する
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>お問い合わせ</title>
        <meta property="og:title" content="お問い合わせ" />
        <meta name="description" content="JOYBASEへのお問い合わせフォーム。技術的な問題、アカウント関連、機能リクエストなど、お気軽にご連絡ください。" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="pt-20">
          <div className="max-w-screen-lg mx-auto px-4 py-8">
            <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
              {/* ヘッダー */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                  <Mail className="w-5 h-5 text-theme-button" />
                </div>
                <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-black">お問い合わせ</h1>
              </div>

              <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600 mb-6">
                ご質問やお困りのことがございましたら、下記フォームよりお気軽にお問い合わせください。
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-100 border-2 border-red-400 rounded-theme">
                  <p className="[font-weight:var(--theme-font-weight-button)] text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <User className="h-4 w-4" />
                    お名前 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="山田太郎"
                    required
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <Mail className="h-4 w-4" />
                    メールアドレス <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="example@example.com"
                    required
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <Phone className="h-4 w-4" />
                    電話番号（任意）
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="090-1234-5678"
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                </div>

                <div>
                  <Label htmlFor="category" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <Tag className="h-4 w-4" />
                    カテゴリ <span className="text-red-500">*</span>
                  </Label>
                  <Select value={category} onValueChange={(value) => setCategory(value as ContactCategory)}>
                    <SelectTrigger className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-theme rounded-theme">
                      {CATEGORY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="[font-weight:var(--theme-font-weight-button)]">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="subject" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <FileText className="h-4 w-4" />
                    件名 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="お問い合わせの件名"
                    required
                    maxLength={200}
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <MessageSquare className="h-4 w-4" />
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="お問い合わせ内容を詳しくご記入ください"
                    required
                    maxLength={5000}
                    rows={8}
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-500 mt-1">最大5000文字</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight)] shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      送信中...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      送信する
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
